import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { apiClient } from '../../utils/apiClient';
import { StatCard } from '../Atelier/components/DiamondStats';
import { BaseModal } from '../../components/shared/BaseModal';
import { FaGem, FaDollarSign, FaChartLine, FaFileContract, FaUsers, FaCheckCircle, FaClock, FaShoppingCart, FaTrophy, FaPercentage } from 'react-icons/fa';
import { PageContainer, PageTitle, Text, SectionTitle, ContentSection } from '../../components/PageStyles';
import styled from 'styled-components';
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;
const ChartCard = styled.div `
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  padding: 24px;
  border-radius: 8px;
`;
const BarChart = styled.div `
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Bar = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;

  .label {
    width: 150px;
    min-width: 150px;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    text-transform: capitalize;
  }

  .bar-container {
    flex: 1;
    height: 32px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
  }

  .bar-fill {
    height: 100%;
    width: ${props => props.$percentage}%;
    background: ${props => props.$color};
    transition: width 0.5s ease;
    display: flex;
    align-items: center;
    padding-left: 12px;
    color: white;
    font-weight: bold;
    font-size: 12px;
  }

  .count {
    min-width: 60px;
    text-align: right;
    color: var(--text-secondary);
    font-size: 14px;
  }
`;
const RecentActivity = styled.div `
  .activity-item {
    padding: 12px;
    border-bottom: 1px solid var(--border-color);
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:hover {
      background: var(--bg-tertiary);
    }

    .activity-text {
      color: var(--text-primary);
      font-size: 14px;
    }

    .activity-time {
      color: var(--text-tertiary);
      font-size: 12px;
    }
  }
`;
const Analytics = () => {
    const { user } = useAuthStore();
    const [demoModalOpen, setDemoModalOpen] = useState(false);
    const [stats, setStats] = useState({
        totalDiamonds: 0,
        totalValue: 0,
        avgPrice: 0,
        totalMemos: 0,
        activeMemos: 0,
        pendingMemos: 0,
        completedMemos: 0,
        totalUsers: 0,
        activeDealers: 0,
        newDealersThisMonth: 0,
        avgDaysToClose: 0,
        conversionRate: 0,
        highValueDiamonds: 0,
        certifiedDiamonds: 0,
        shapes: {},
        colors: {}
    });
    useEffect(() => {
        loadAnalytics();
    }, []);
    const loadAnalytics = async () => {
        try {
            // Load diamonds
            const diamondsRes = await apiClient.get('diamonds-inventory?limit=1000');
            if (diamondsRes.ok && diamondsRes.data?.success) {
                const diamonds = diamondsRes.data.data.diamonds;
                const shapes = {};
                const colors = {};
                let totalValue = 0;
                let highValueCount = 0;
                let certifiedCount = 0;
                diamonds.forEach((d) => {
                    shapes[d.shape] = (shapes[d.shape] || 0) + 1;
                    colors[d.color] = (colors[d.color] || 0) + 1;
                    totalValue += d.priceUSD || 0;
                    if (d.priceUSD > 50000)
                        highValueCount++;
                    if (d.certified || d.lab)
                        certifiedCount++;
                });
                setStats(prev => ({
                    ...prev,
                    totalDiamonds: diamonds.length,
                    totalValue,
                    avgPrice: totalValue / diamonds.length,
                    highValueDiamonds: highValueCount,
                    certifiedDiamonds: certifiedCount,
                    shapes,
                    colors
                }));
            }
            // Load memos (if admin)
            if (user && user.roleLevel >= 7) {
                const memosRes = await apiClient.get('memos/all');
                if (memosRes.ok && memosRes.data?.memos) {
                    const memos = memosRes.data.memos;
                    const pending = memos.filter((m) => m.status === 'pending').length;
                    const active = memos.filter((m) => m.status === 'with_client' || m.status === 'approved' || m.status === 'shipped').length;
                    const completed = memos.filter((m) => m.status === 'returned' || m.status === 'sold').length;
                    const sold = memos.filter((m) => m.status === 'sold').length;
                    setStats(prev => ({
                        ...prev,
                        totalMemos: memos.length,
                        pendingMemos: pending,
                        activeMemos: active,
                        completedMemos: completed,
                        conversionRate: completed > 0 ? (sold / completed) * 100 : 0
                    }));
                }
                // Load users
                const usersRes = await apiClient.get('users-list');
                if (usersRes.ok && usersRes.data?.users) {
                    const users = usersRes.data.users;
                    const activeDealers = users.filter((u) => u.roleLevel >= 4 && u.status === 'active').length;
                    const thirtyDaysAgo = new Date();
                    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                    const newDealers = users.filter((u) => new Date(u.createdAt) > thirtyDaysAgo && u.roleLevel >= 3).length;
                    setStats(prev => ({
                        ...prev,
                        totalUsers: users.length,
                        activeDealers,
                        newDealersThisMonth: newDealers
                    }));
                }
            }
        }
        catch (error) {
            console.error('Failed to load analytics:', error);
        }
    };
    const topShapes = Object.entries(stats.shapes)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);
    const maxShapeCount = topShapes[0]?.[1] || 1;
    const topColors = Object.entries(stats.colors)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);
    const maxColorCount = topColors[0]?.[1] || 1;
    return (_jsxs(PageContainer, { children: [_jsx(PageTitle, { children: "Platform Analytics" }), _jsx(Text, { children: "Real-time insights into inventory, user activity, and platform performance" }), _jsx("div", { style: { marginBottom: '24px' }, children: _jsx("button", { onClick: () => setDemoModalOpen(true), style: {
                        padding: '12px 24px',
                        background: 'var(--accent-color)',
                        color: 'var(--bg-primary)',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                    }, children: "Preview BaseModal Component" }) }), _jsx(BaseModal, { isOpen: demoModalOpen, onClose: () => setDemoModalOpen(false), title: "BaseModal Component Demo", maxWidth: "600px", children: _jsxs("div", { children: [_jsx("h3", { style: { color: 'var(--text-primary)', marginTop: 0 }, children: "Reusable Modal Component" }), _jsx("p", { style: { color: 'var(--text-secondary)', lineHeight: 1.6 }, children: "This is the new BaseModal component that provides consistent styling for all modals across the platform." }), _jsx("h4", { style: { color: 'var(--text-primary)' }, children: "Features:" }), _jsxs("ul", { style: { color: 'var(--text-secondary)' }, children: [_jsx("li", { children: "Solid dark overlay with blur effect" }), _jsx("li", { children: "Theme-aware styling" }), _jsx("li", { children: "Sticky header with close button" }), _jsx("li", { children: "Configurable width and padding" }), _jsx("li", { children: "Click outside to close" }), _jsx("li", { children: "Smooth animations" })] }), _jsxs("div", { style: { marginTop: '24px', padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '4px' }, children: [_jsx("strong", { style: { color: 'var(--text-primary)' }, children: "Theme-aware:" }), _jsx("p", { style: { color: 'var(--text-secondary)', margin: '8px 0 0 0' }, children: "Try toggling dark/light mode - this modal adapts automatically!" })] })] }) }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Inventory Overview" }), _jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaGem, { style: { marginRight: '8px', color: '#3b82f6' } }), stats.totalDiamonds] }), _jsx("div", { className: "stat-label", children: "Total Inventory" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", style: { display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: [_jsx(FaDollarSign, { style: { marginRight: '8px', color: '#10b981' } }), _jsxs("span", { children: ["$", (stats.totalValue / 1000000).toFixed(2), "M"] })] }), _jsx("div", { className: "stat-label", children: "Total Value" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaChartLine, { style: { marginRight: '8px', color: '#f59e0b' } }), "$", (stats.avgPrice / 1000).toFixed(1), "K"] }), _jsx("div", { className: "stat-label", children: "Average Price" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaTrophy, { style: { marginRight: '8px', color: '#8b5cf6' } }), stats.highValueDiamonds] }), _jsx("div", { className: "stat-label", children: "Premium ($50K+)" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaCheckCircle, { style: { marginRight: '8px', color: '#22c55e' } }), stats.certifiedDiamonds] }), _jsx("div", { className: "stat-label", children: "Certified Stones" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaPercentage, { style: { marginRight: '8px', color: '#06b6d4' } }), ((stats.certifiedDiamonds / stats.totalDiamonds) * 100).toFixed(1), "%"] }), _jsx("div", { className: "stat-label", children: "Certification Rate" })] })] })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Memo Performance" }), _jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaFileContract, { style: { marginRight: '8px', color: '#3b82f6' } }), stats.totalMemos] }), _jsx("div", { className: "stat-label", children: "Total Requests" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaClock, { style: { marginRight: '8px', color: '#f59e0b' } }), stats.pendingMemos] }), _jsx("div", { className: "stat-label", children: "Pending Approval" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaShoppingCart, { style: { marginRight: '8px', color: '#10b981' } }), stats.activeMemos] }), _jsx("div", { className: "stat-label", children: "Active with Clients" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaCheckCircle, { style: { marginRight: '8px', color: '#22c55e' } }), stats.completedMemos] }), _jsx("div", { className: "stat-label", children: "Completed" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaPercentage, { style: { marginRight: '8px', color: '#8b5cf6' } }), stats.conversionRate.toFixed(1), "%"] }), _jsx("div", { className: "stat-label", children: "Conversion Rate" })] })] })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Dealer Network" }), _jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaUsers, { style: { marginRight: '8px', color: '#3b82f6' } }), stats.totalUsers] }), _jsx("div", { className: "stat-label", children: "Total Users" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaCheckCircle, { style: { marginRight: '8px', color: '#22c55e' } }), stats.activeDealers] }), _jsx("div", { className: "stat-label", children: "Active Dealers" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaTrophy, { style: { marginRight: '8px', color: '#f59e0b' } }), stats.newDealersThisMonth] }), _jsx("div", { className: "stat-label", children: "New This Month" })] })] })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Most Popular Shapes" }), _jsx(ChartCard, { children: _jsx(BarChart, { children: topShapes.map(([shape, count]) => (_jsxs(Bar, { "$percentage": (count / maxShapeCount) * 100, "$color": "#3b82f6", children: [_jsx("div", { className: "label", children: shape }), _jsx("div", { className: "bar-container", children: _jsxs("div", { className: "bar-fill", children: [count, " diamonds"] }) }), _jsxs("div", { className: "count", children: [((count / stats.totalDiamonds) * 100).toFixed(1), "%"] })] }, shape))) }) })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Most Common Colors" }), _jsx(ChartCard, { children: _jsx(BarChart, { children: topColors.map(([color, count]) => (_jsxs(Bar, { "$percentage": (count / maxColorCount) * 100, "$color": "#10b981", children: [_jsxs("div", { className: "label", children: ["Color ", color] }), _jsx("div", { className: "bar-container", children: _jsxs("div", { className: "bar-fill", children: [count, " diamonds"] }) }), _jsxs("div", { className: "count", children: [((count / stats.totalDiamonds) * 100).toFixed(1), "%"] })] }, color))) }) })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Platform Summary" }), _jsxs(RecentActivity, { children: [_jsxs("div", { className: "activity-item", children: [_jsxs("span", { className: "activity-text", children: [_jsx(FaGem, { style: { marginRight: '8px', color: '#3b82f6' } }), stats.totalDiamonds, " diamonds available for dealer browsing"] }), _jsx("span", { className: "activity-time", children: "Live" })] }), _jsxs("div", { className: "activity-item", children: [_jsxs("span", { className: "activity-text", children: [_jsx(FaFileContract, { style: { marginRight: '8px', color: '#f59e0b' } }), stats.activeMemos, " memo requests currently with clients"] }), _jsx("span", { className: "activity-time", children: "Live" })] }), _jsxs("div", { className: "activity-item", children: [_jsxs("span", { className: "activity-text", children: [_jsx(FaUsers, { style: { marginRight: '8px', color: '#8b5cf6' } }), stats.activeDealers, " active dealers on platform"] }), _jsx("span", { className: "activity-time", children: "Live" })] }), _jsxs("div", { className: "activity-item", children: [_jsxs("span", { className: "activity-text", children: [_jsx(FaDollarSign, { style: { marginRight: '8px', color: '#10b981' } }), "$", (stats.totalValue / 1000000).toFixed(2), "M total inventory value under management"] }), _jsx("span", { className: "activity-time", children: "Live" })] }), _jsxs("div", { className: "activity-item", children: [_jsxs("span", { className: "activity-text", children: [_jsx(FaTrophy, { style: { marginRight: '8px', color: '#f59e0b' } }), stats.newDealersThisMonth, " new dealer", stats.newDealersThisMonth !== 1 ? 's' : '', " joined this month"] }), _jsx("span", { className: "activity-time", children: "30 Days" })] }), _jsxs("div", { className: "activity-item", children: [_jsxs("span", { className: "activity-text", children: [_jsx(FaPercentage, { style: { marginRight: '8px', color: '#22c55e' } }), stats.conversionRate.toFixed(1), "% memo to sale conversion rate"] }), _jsx("span", { className: "activity-time", children: "All Time" })] })] })] })] }));
};
export default Analytics;
